<template>
  <TopNavigation
    previous-route="Dilemmas"
    :title="$t('components.comment.title', 'Comments')"
  />

  <div class="container mx-auto min-h-screen px-3 pb-28 pt-24">
    <!-- Dilemma Card -->
    <div v-if="dilemmaDoc" class="mb-6 w-full">
      <DilemmaListItem :dilemma="dilemmaDoc" simple />
    </div>

    <InformationText
      v-if="fetchError"
      color="red"
      class="mb-2"
      :information="fetchError"
    />

    <SkeletonLoader v-if="loading" />

    <!-- Comments List -->
    <div v-if="dilemmaDoc" class="flex flex-col gap-2">
      <Comment
        v-for="(comment, index) in comments"
        :key="`${comment.author}-${index}`"
        :author-uuid="comment.author"
        :previous-date="getPreviousDate(index)"
        :date="comment.created_at"
        :comment="comment.comment"
      />
    </div>
  </div>

  <!-- Bottom Comment Input -->
  <div class="fixed bottom-0 left-0 z-20 flex w-full justify-center">
    <div class="w-full max-w-xl bg-white px-3 pb-4 dark:bg-kekkle-dark">
      <CommentInput :posting-comment="posting" @post-comment="postNewComment" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import TopNavigation from "@/components/TopNavigation.vue";
import CommentInput from "@/components/CommentInput.vue";
import Comment from "@/components/Comment.vue";
import InformationText from "@/components/InformationText.vue";
import SkeletonLoader from "@/components/ui/SkeletonLoader.vue";
import DilemmaListItem from "@/views/games/dilemmas/components/DilemmaListItem.vue";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useUserStore } from "@/stores/modules/user/user";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useDilemmasStore } from "@/stores/modules/dilemmas/dilemmas";
import { useComments } from "@/composables/useComments";
import { dilemmaCommentDocumentConverter } from "@kekkle/shared/convertors/web/comment-document.converter";
import router from "@/router/router";

type Props = {
  dilemmaId: string;
  groupId: string;
};

const props = defineProps<Props>();

const authStore = useAuthStore();
const userStore = useUserStore();
const groupsStore = useGroupsStore();
const dilemmasStore = useDilemmasStore();

const dilemmaDoc = computed(
  () =>
    dilemmasStore.allDilemmas.find((d) => d._id === props.dilemmaId) ?? null,
);

const {
  comments,
  posting,
  fetchError,
  loading,
  postComment: postCommentToFirestore,
} = useComments({
  groupId: props.groupId,
  itemId: props.dilemmaId,
  collectionName: "dilemmas_comments",
  converter: dilemmaCommentDocumentConverter,
  incrementField: "number_of_comments",
  authorUuid: authStore.authUuid ?? "",
});

boot();

watch(
  () => comments.value.length,
  () => {
    setTimeout(() => {
      window.scrollTo({
        behavior: "smooth",
        left: 0,
        top: document.body.scrollHeight,
      });
    }, 40);
  },
  { immediate: true },
);

async function boot(): Promise<void> {
  if (userStore.userData?.["groups"]?.includes(props.groupId)) {
    if (userStore.activeGroup !== props.groupId) {
      if (!authStore.authUuid) {
        await router.push({ name: "Group" });
        return;
      }
      await userStore.switchActiveGroup(props.groupId);
      await groupsStore.refreshAllUserGroupsData();
    }
    dilemmasStore.subscribeToDilemmas();
  } else {
    await router.push({ name: "Group" });
  }
}

function getPreviousDate(index: number): Date | undefined {
  if (index === 0) return undefined;
  return comments.value[index - 1]?.created_at;
}

async function postNewComment(e: { comment: string }): Promise<void> {
  await postCommentToFirestore(e.comment);
}
</script>
